import React from 'react';
import { useActivityLogsByEntity } from '../useData';
import { formatDateTime } from '../../../config/appConfig';
import { Badge } from '../../../components/ui';
import { Link } from 'react-router-dom';

interface ActivityLogSectionProps {
  entityType: string;
  entityId: number;
  limit?: number;
}

const ActivityLogSection: React.FC<ActivityLogSectionProps> = ({
  entityType,
  entityId,
  limit = 10,
}) => {
  const { data, isLoading } = useActivityLogsByEntity(entityType, entityId);

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500">Loading activity logs...</div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500">No activity logs found.</div>
      </div>
    );
  }

  const displayLogs = data.slice(0, limit);

  const ACTION_LABELS: Record<string, string> = {
    CREATE: 'Create',
    UPDATE: 'Update',
    DELETE: 'Delete',
  };
  const getActionBadgeColor = (action: string): 'default' | 'secondary' | 'accent' | 'danger' | 'warning' | 'info' => {
    switch (action) {
      case 'CREATE':
        return 'accent';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'danger';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Activity Log</h3>
        {data.length > limit && (
          <Link
            to={`/activity-logs?entityType=${entityType}&entityId=${entityId}`}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            View All ({data.length})
          </Link>
        )}
      </div>
      <div className="space-y-2">
        {displayLogs.map((log) => {
          const userName = log.user?.userDisplayName || 
            `${log.user?.userFirstName || ''} ${log.user?.userLastName || ''}`.trim() ||
            log.user?.userEmail ||
            'Unknown';

          return (
            <div
              key={log.id}
              className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={getActionBadgeColor(log.action)}>
                      {ACTION_LABELS[log.action] ?? log.action}
                    </Badge>
                    <span className="text-sm font-medium">{userName}</span>
                    <span className="text-xs text-gray-500">
                      {formatDateTime(log.createdAt)}
                    </span>
                  </div>
                  {log.changes && (
                    <div className="text-xs text-gray-600 mt-1">
                      {log.action === 'CREATE' && 'Created new record'}
                      {log.action === 'UPDATE' && 'Updated record'}
                      {log.action === 'DELETE' && 'Deleted record'}
                    </div>
                  )}
                </div>
                <Link
                  to={`/activity-logs/${log.id}`}
                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                >
                  View Details
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityLogSection;
